import { Context, ContextInternal } from "../context/context";
import { ContextServiceImpl, previousContextIdSessionStorageKey } from "../context/context-service";
import { RecoveryPage, RecoveryPageParameters, recoveryStateName } from "../../recovery/recovery-page";
import { open as WindowStatusOpen } from "../../../ShellCommon/eventModule/window-heartbeat-service";

/** A service to recover closed/crashed ServiceDesk windows from their Context (See ContextService) */
export interface RecoveryService {
  /** Get all inactive contexts that can be restored */
  recoverableContexts(): ng.IPromise<Context[]>;

  /** Restore a context by id
   * @param id The context id, can be looked up via the ContextService
   * @param options Options for the behavoir when opening the window
   * @throws If the option fallback state was specificed that was invalid
   */
  restoreContext(id: string, options?: RecoveryOptions): void;
}

/** Options while restoring a context */
export interface RecoveryOptions {
  /** Restore the context in the current window, otherwise opens in a new window */
  restoreInCurrentWindow?: boolean;

  /** If the context was not able to be restored, navigate to this state instead of showing an error message */
  fallbackState?: string;
}

export class RecoveryServiceImpl implements RecoveryService {
  // @ngInject
  constructor(
    private $window: ng.IWindowService,
    private $state: ng.ui.IStateService,
    private contextService: ContextServiceImpl
  ) {
    this.recoverIfRefresh();
  }

  private recoverIfRefresh() {
    let previousContextId = this.$window.sessionStorage.getItem(previousContextIdSessionStorageKey);
    if (previousContextId) {
      this.contextService.context(previousContextId).then((matchingContext: any) => {
        if (matchingContext && matchingContext.stateName == this.$state.current.name) {
          this.contextService.recoverContext(previousContextId);
        }
      });
    }
  }

  public recoverableContexts() {
    return this.contextService
      .contexts()
      .then((contexts: ContextInternal[]) => contexts.filter((c) => c.status != WindowStatusOpen));
  }

  public restoreContext(id: string, options: RecoveryOptions = {}) {
    let windowTarget = "_blank";
    let replaceDocumentHistory = false;

    if (options && options.restoreInCurrentWindow) {
      windowTarget = "_self";
      replaceDocumentHistory = true;
    }

    let recoveryParams: RecoveryPageParameters = {
      contextId: id,
      fallbackState: options.fallbackState
    };

    if (recoveryParams.fallbackState && !this.$state.get(recoveryParams.fallbackState)) {
      throw new Error(`Invalid fallback state ${recoveryParams.fallbackState}`);
    }

    let recoveryUrl = this.$state.href(recoveryStateName, recoveryParams);
    this.$window.open(recoveryUrl, windowTarget, undefined, replaceDocumentHistory);
  }
}
